import { DATA_TYPE, DEFAULT_REPORT } from './constants';
import { subDays, format, ISODateFormat, yesterday } from '../../../utils/date';

const metricByExistingName = {
  arpu: 'avg_revenue_per_user',
  eng: 'engagement',
  spu: 'sessions_per_user',
  total_time_spent: 'total_time',
  at_spu: 'avg_session_len',
};

const basePredefinedFields = {
  data_type: 'base',
  data_points: ['downloads', 'revenue', 'avg_revenue_per_user', 'iap_revenue', 'dau', 'mau', 'engagement'],
  growth_rates: [],
  countries_iso: ['WW'],
  include_ww: false,
  geo_granularity: 'custom',
  data_granularity: 'daily',
  data_schema: 'app_unified',
  split_by_acquisition: true,
  output_format: 'vertical',
  export_type: 'csv',
};

const getPredefinedReport = (pageName) => {
  switch (pageName) {
    // App Insights
    case 'app_insights_summary_show':
    case 'apps_performance':
      return {
        ...basePredefinedFields,
        data_points: ['downloads', 'total_revenue', 'avg_revenue_per_user', 'iap_revenue', 'dau', 'mau', 'engagement'],
      };
    case 'apps_engagement':
      return {
        ...basePredefinedFields,
        data_points: ['sessions', 'avg_session_len', 'sessions_per_user', 'dau', 'mau', 'engagement', 'total_time'],
      };
    case 'apps_downloads_and_revenue':
      return {
        ...basePredefinedFields,
        data_points: ['downloads', 'total_revenue', 'avg_revenue_per_user', 'iap_revenue', 'download_revenue'],
      };
    case 'rank_history_show':
      return {
        ...basePredefinedFields,
        data_points: ['daily_app_star_ratings', 'daily_app_category_ranks'],
        data_schema: 'app_store',
      };
    case 'apps_reviews':
    case 'review_analysis/app_reviews_sentiments_show':
    case 'review_analysis/app_reviews_intents_show':
    case 'review_analysis/app_reviews_keywords_show':
    case 'reviews': // Market Reviews page
      return {
        ...basePredefinedFields,
        data_points: ['review_intents', 'review_sentiments', 'review_keywords'],
      };
    // Markets pages
    case 'performance':
    case 'tracker':
      return basePredefinedFields;
    case 'rank':
      return {
        ...basePredefinedFields,
        data_points: ['daily_app_star_ratings', 'daily_app_category_ranks'],
        geo_granularity: 'country',
      };
    case 'overview':
      return {
        ...basePredefinedFields,
        data_points: ['downloads', 'total_revenue', 'sessions', 'dau', 'mau', 'total_time'],
      };
    // Company page
    case 'company':
      return {
        ...basePredefinedFields,
        data_points: ['downloads', 'sessions', 'dau', 'mau'],
        data_schema: 'parent_company_unified',
        split_by_acquisition: false,
        start_date: format(subDays(yesterday(), 730 - 1), ISODateFormat),
        end_date: format(yesterday(), ISODateFormat),
      };
    // Publisher page
    case 'publisher':
      return {
        ...basePredefinedFields,
        data_points: [
          'downloads',
          'sessions',
          'dau',
          'mau',
          'iap_revenue',
          'total_time',
          'engagement',
          'avg_revenue_per_user',
          'sessions_per_user',
          'total_revenue',
          'avg_session_len',
          'download_revenue',
        ],
        data_schema: 'parent_company_unified',
        split_by_acquisition: false,
      };
    default:
      return {};
  }
};

export const normalizeExportMetrics = (metrics) => {
  if (!metrics) return [];

  return metrics.reduce((acc, metric) => {
    if (!metric) return acc;

    return [...acc, metricByExistingName[metric?.toLowerCase()] || metric];
  }, []);
};

export const makeReport = ({
  initParams = {},
  reportParams = {},
}) => {
  const initialReport = { ...getPredefinedReport(initParams.pageName), ...reportParams };

  const dataTypes = Object.values(DATA_TYPE);
  const initReportParams = dataTypes.reduce((result, dataType) => ({
    ...result,
    [dataType]: dataType === initialReport.data_type ? {
      ...DEFAULT_REPORT[dataType],
      ...initialReport,
      data_points: normalizeExportMetrics(initialReport.data_points),
    } : { ...DEFAULT_REPORT[dataType] },
  }), {});

  return {
    ...initParams,
    dataType: initialReport.data_type,
    report: initReportParams,
  };
};
